import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Battery, Sun, Zap, Users, Shield, Clock, TrendingUp, MessageSquare, ChevronLeft, ChevronRight, Star, Quote, Award, Target, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ratgeberArticles } from '../data/ratgeberArticles';
import { useState, useRef } from 'react';
import { ResponsiveImage } from '../components/ResponsiveImage';

const LOGO_URL = "/assets/ENPOWER-on-dark.svg";
const HERO_IMAGE = "/assets/solar-roof-hero-1200.jpg";

const stats = [
  { value: '100+', label: 'Projekte im Partnernetzwerk', icon: Award },
  { value: '70+', label: 'betreute Flächeneigentümer', icon: Users },
  { value: '10M€+', label: 'Projektwert mit Partnern', icon: TrendingUp },
  { value: '10+', label: 'Jahre gebündelte Erfahrung', icon: Shield },
];

const services = [
  {
    icon: Zap,
    title: 'Kleinspeicher / Schwarmspeicher',
    subtitle: 'ab 5m²',
    description: 'Kleine Speicherflächen wirtschaftlich nutzen: einmalig oder jährlich, skalierbar und ohne eigene Investition.',
    link: '/leistungen#schwarmspeicher'
  },
  {
    icon: Battery,
    title: 'Batteriespeicher (BESS)',
    subtitle: 'ab 80m²',
    description: 'Große Batteriespeicher für Gewerbe-, Industrie- und geeignete Freiflächen mit attraktiven Pachtmodellen.',
    link: '/leistungen#batteriespeicher'
  },
  {
    icon: Sun,
    title: 'Freiflächen-PV',
    subtitle: 'ab 1 Hektar',
    description: 'Acker, Grünland, Brache oder Gewerbefläche langfristig für Photovoltaik-Projekte erschließen.',
    link: '/leistungen#freiflaechen'
  },
  {
    icon: Building2,
    title: 'Dachverpachtung',
    subtitle: 'ab 400m²',
    description: 'Dachflächen als Einnahmequelle nutzen, optional auch mit Dachsanierung statt Pachtzahlung.',
    link: '/leistungen#dachverpachtung'
  },
];

const values = [
  { icon: Zap, title: 'Schnelle Abwicklung', description: 'Von der Anfrage bis zum Vertrag in kürzester Zeit' },
  { icon: Users, title: 'Individuelle Beratung', description: 'Persönliche Betreuung auf Augenhöhe' },
  { icon: Shield, title: 'Gebündelte Erfahrung', description: 'EN:POWER-Kompetenz plus geprüfte Partnerexpertise' },
  { icon: Clock, title: 'Rundum-Service', description: 'Alles aus einer Hand – von A bis Z' },
  { icon: TrendingUp, title: 'Zukunftssichere Strategien', description: 'Langfristig profitable Partnerschaften' },
  { icon: MessageSquare, title: 'Transparente Kommunikation', description: 'Klare Prozesse und offener Dialog' },
];

const testimonials = [
  {
    name: 'Thomas Müller',
    role: 'Geschäftsführer, Logistik GmbH',
    content: 'Die Zusammenarbeit mit EN:POWER war von Anfang an professionell und unkompliziert. Unser Hallendach generiert jetzt passive Einnahmen – ohne dass wir einen Finger krümmen mussten.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face',
    imageWidth: 150,
    imageHeight: 150,
  },
  {
    name: 'Maria Schmidt',
    role: 'Landwirtin',
    content: 'Dank EN:POWER nutzen wir unsere Brachfläche jetzt sinnvoll. Die Pachteinnahmen sind ein willkommenes Zusatzeinkommen für unseren Betrieb.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face',
    imageWidth: 150,
    imageHeight: 150,
  },
  {
    name: 'Stefan Weber',
    role: 'Immobilienentwickler',
    content: 'Als Partner für unsere Batteriespeicher-Projekte ist EN:POWER unschlagbar. Schnell, kompetent und immer fair in den Verhandlungen.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    imageWidth: 150,
    imageHeight: 150,
  },
];

const teamMembers = [
  {
    name: 'Arne Mallmann',
    role: 'Sales',
    image: '/assets/team-arne-mallmann.jpeg',
    imageWidth: 984,
    imageHeight: 1416,
  },
  {
    name: 'Michael Doerdrechter',
    role: 'Operations & Sales',
    image: '/assets/team-michael-doerdrechter.jpeg',
    imageWidth: 1366,
    imageHeight: 2048,
  },
  {
    name: 'Lee-Ann Sophie Klein',
    role: 'Geschäftsführerin',
    image: '/assets/team-lee-ann-klein.png',
    imageWidth: 1023,
    imageHeight: 1537,
  },
  {
    name: 'Randy Wesley Kirsten',
    role: 'Geschäftsführer',
    image: '/assets/team-randy-kirsten.png',
    imageWidth: 1023,
    imageHeight: 1538,
  },
  {
    name: 'Kilian Elitzke',
    role: 'Sales',
    image: '/assets/team-kilian-elitzke.jpeg',
    imageWidth: 1145,
    imageHeight: 1600,
  },
  {
    name: 'Lennart Laabs',
    role: 'Marketing',
    image: '/assets/team-lennart-laabs.jpg',
    imageWidth: 1000,
    imageHeight: 1500,
  },
  {
    name: 'Lukas Müller',
    role: 'Sales',
    image: '/assets/team-lukas-mueller.jpg',
    imageWidth: 1000,
    imageHeight: 1500,
  },
];

const partnerLogos = [
  { name: 'ILOS Energy', src: '/assets/partner-ilos.png', width: 3710, height: 1773 },
  { name: 'Solarea', src: '/assets/partner-solarea.png', width: 1672, height: 495 },
  { name: 'Solarverse', src: '/assets/partner-solarverse.svg', width: 772, height: 167 },
  { name: 'Adversum', src: '/assets/partner-adversum.png', width: 1500, height: 425 },
];

const partnerLogoSet = [...partnerLogos, ...partnerLogos, ...partnerLogos];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center bg-[#122A5C] overflow-hidden" data-testid="hero-section">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-[600px] h-[600px] bg-[#E07000]/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#E07000]/10 rounded-full blur-[150px]" />
        </div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <motion.div 
          style={{ y: heroY, opacity: heroOpacity }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10"
        >
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
                Ihre Fläche.
                <br />
                <span className="text-[#E07000]">Unsere Expertise.</span>
                <br />
                <span className="text-white/90">Gemeinsamer Erfolg.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-white/70 mb-10 max-w-lg leading-relaxed">
                Wir entwickeln wirtschaftlich attraktive Energieprojekte auf Ihren Dach- und Freiflächen. 
                Profitieren Sie von garantierten Pachteinnahmen – ohne eigene Investition.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/pachtrechner">
                  <Button 
                    data-testid="hero-calculator-button"
                    className="bg-[#E07000] hover:bg-[#C56200] text-white rounded-full px-8 py-6 text-lg font-semibold btn-animate shadow-xl shadow-orange-600/30 w-full sm:w-auto group"
                  >
                    Pachtpotenzial berechnen
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/leistungen">
                  <Button 
                    data-testid="hero-services-button"
                    variant="outline"
                    className="border-2 border-white/30 text-white hover:bg-white hover:text-[#122A5C] rounded-full px-8 py-6 text-lg font-semibold transition-all w-full sm:w-auto backdrop-blur-sm"
                  >
                    Unsere Leistungen
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Content - Image with Stats */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="relative"
            >
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <ResponsiveImage
                    src={HERO_IMAGE}
                    alt="Industrielle Solaranlage auf Dach"
                    width={1200}
                    height={900}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-[450px] lg:h-[550px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#122A5C]/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Stats Cards */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="absolute -bottom-6 left-4 right-4 md:left-6 md:right-6"
                >
                  <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-2xl" data-testid="stats-card">
                    <div className="grid grid-cols-4 gap-4">
                      {stats.map((stat, index) => (
                        <motion.div 
                          key={index} 
                          className="text-center"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.8 + index * 0.1 }}
                        >
                          <stat.icon className="w-5 h-5 text-[#E07000] mx-auto mb-2" />
                          <div className="text-xl md:text-2xl font-bold text-[#122A5C]">{stat.value}</div>
                          <div className="text-[10px] md:text-xs text-slate-500 mt-1 leading-tight">{stat.label}</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#E07000] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Partner Logo Strip - BEFORE Empowerment Section */}
      <section className="py-12 bg-white border-b border-slate-100" data-testid="partner-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-sm text-slate-500 mb-8 uppercase tracking-[0.2em] font-semibold"
          >
            Strategische Partner
          </motion.p>
          <div className="marquee-container">
            <div className="marquee-content">
              {[0, 1].map((loop) => (
                <div key={loop} className="flex flex-shrink-0 items-center gap-16 px-8">
                  {partnerLogoSet.map((partner, index) => (
                    <div key={`${partner.name}-${loop}-${index}`} className="flex-shrink-0">
                      <div className="w-44 h-20 bg-white rounded-xl flex items-center justify-center transition-all duration-300 border border-slate-200 shadow-sm hover:shadow-md px-5">
                        <ResponsiveImage
                          src={partner.src}
                          alt={`${partner.name} Logo`}
                          width={partner.width}
                          height={partner.height}
                          sizes="176px"
                          loading="lazy"
                          decoding="async"
                          className="max-w-full max-h-14 object-contain"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
                  </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
              Unsere Leistungen
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#122A5C] mb-6">
              Flächen intelligent nutzen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Von Kleinspeichern bis zur Dachverpachtung: Wir entwickeln passende Modelle für Ihre Fläche.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Link to={service.link}>
                  <Card 
                    className="service-card h-full border border-slate-200 rounded-2xl overflow-hidden card-hover group cursor-pointer bg-white hover:border-[#E07000] hover:shadow-xl transition-all duration-300"
                    data-testid={`service-card-${index}`}
                  >
                    <CardContent className="p-8 md:p-10">
                      <div className="w-16 h-16 bg-[#122A5C] rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#E07000] transition-colors duration-300">
                        <service.icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex flex-wrap items-baseline gap-3 mb-4">
                        <h3 className="font-heading text-2xl font-bold text-[#122A5C]">{service.title}</h3>
                        <span className="text-sm font-semibold text-[#E07000]">{service.subtitle}</span>
                      </div>
                      <p className="text-slate-600 leading-relaxed text-lg">{service.description}</p>
                      <div className="mt-8 flex items-center text-[#122A5C] font-semibold group-hover:text-[#E07000] transition-colors">
                        <span>Mehr erfahren</span>
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {false && (
        <>
      {/* Ratgeber Section */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="ratgeber-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-10 flex flex-col gap-6 md:mb-14 md:flex-row md:items-end md:justify-between"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
                Ratgeber
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#122A5C] mb-5">
                Wissen für Ihre Fläche
              </h2>
              <p className="text-lg text-slate-600">
                Praktische Orientierung für Eigentümer in Magdeburg, Sachsen-Anhalt und Deutschland.
              </p>
            </div>
            <Link to="/ratgeber">
              <Button
                variant="outline"
                className="rounded-full border-2 border-[#122A5C] px-7 py-5 font-semibold text-[#122A5C] hover:bg-[#122A5C] hover:text-white"
              >
                Alle Artikel
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {ratgeberArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/ratgeber/${article.slug}`}
                className="group flex min-h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all hover:border-[#E07000] hover:shadow-xl"
                data-testid={`home-ratgeber-card-${article.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <ResponsiveImage
                    src={article.image}
                    alt={article.imageAlt}
                    width={article.imageWidth}
                    height={article.imageHeight}
                    sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#E07000]">
                    <BookOpen className="h-4 w-4" />
                    {article.category}
                  </div>
                  <h3 className="font-heading mb-3 text-xl font-bold leading-tight text-[#122A5C]">
                    {article.title}
                  </h3>
                  <p className="mb-5 flex-1 text-sm leading-relaxed text-slate-600">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center font-semibold text-[#122A5C] transition-colors group-hover:text-[#E07000]">
                    Lesen
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

        </>
      )}

      {/* Empowerment Mission Statement */}
      <section className="py-20 bg-slate-50" data-testid="mission-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
              <span className="text-[#122A5C]">EN:</span><span className="text-[#E07000]">POWER</span>
              <span className="text-[#122A5C]"> = </span>
              <span className="italic text-[#E07000]">Empowerment</span>
            </h2>

            <p className="text-xl md:text-2xl text-[#122A5C] font-medium mb-6 max-w-3xl mx-auto">
              Wir machen Sie zum Teil der Energiewende.
            </p>

            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-10">
              Jede Fläche zählt. Jeder Partner macht den Unterschied.
              Gemeinsam schaffen wir Deutschlands Energie-Unabhängigkeit.
            </p>

            <Link to="/ueber-uns">
              <Button
                variant="outline"
                className="border-2 border-[#122A5C] text-[#122A5C] hover:bg-[#122A5C] hover:text-white rounded-full px-8 py-4 text-lg font-semibold transition-all"
              >
                Mehr über uns erfahren
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
              Warum <span className="text-[#122A5C]">EN:</span>POWER
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#122A5C]">
              Unsere Werte, Ihr Vorteil
            </h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 rounded-2xl overflow-hidden"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white p-8 md:p-10 hover:bg-slate-50 transition-colors group"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#122A5C] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#E07000] transition-colors">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#122A5C] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Section - Carousel */}
      <section className="py-24 md:py-32 bg-white" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
              Unser Team
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#122A5C] mb-6">
              Experten an Ihrer Seite
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Unser erfahrenes Team begleitet Sie von der ersten Beratung bis zur erfolgreichen Projektumsetzung.
            </p>
          </motion.div>

          {/* Team Carousel */}
          <div className="team-marquee-container overflow-hidden">
            <div className="team-marquee-content flex animate-team-scroll" style={{ '--team-card-count': teamMembers.length }}>
              {[...teamMembers, ...teamMembers, ...teamMembers].map((member, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 mx-4 w-64"
                  data-testid={`team-member-${index % teamMembers.length}`}
                >
                  <div className="bg-slate-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <div className="aspect-[3/4] overflow-hidden">
                      {member.image ? (
                        <ResponsiveImage
                          src={member.image}
                          alt={member.name}
                          width={member.imageWidth}
                          height={member.imageHeight}
                          sizes="256px"
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#122A5C] flex items-center justify-center">
                          <span className="text-5xl font-bold text-white">{member.initials}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-5 text-center">
                      <h3 className="font-heading font-bold text-[#122A5C] text-lg">{member.name}</h3>
                      <p className="text-sm text-[#E07000] font-semibold mt-1">{member.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
              Kundenstimmen
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#122A5C]">
              Was unsere Partner sagen
            </h2>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-3xl p-8 md:p-12 border border-slate-200 shadow-lg"
              data-testid="testimonial-card"
            >
              <Quote className="w-16 h-16 text-[#E07000]/20 mb-6" />
              <p className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-8 font-medium">
                "{testimonials[currentTestimonial].content}"
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].name}
                  width={testimonials[currentTestimonial].imageWidth}
                  height={testimonials[currentTestimonial].imageHeight}
                  loading="lazy"
                  decoding="async"
                  className="w-16 h-16 rounded-full object-cover ring-4 ring-[#E07000]/20"
                />
                <div>
                  <h4 className="font-bold text-[#122A5C] text-lg">{testimonials[currentTestimonial].name}</h4>
                  <p className="text-slate-600">{testimonials[currentTestimonial].role}</p>
                </div>
                <div className="ml-auto flex gap-1">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-[#E07000] text-[#E07000]" />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Navigation */}
            <div className="flex justify-center gap-4 mt-10">
              <button 
                onClick={prevTestimonial}
                aria-label="Vorherige Kundenstimme anzeigen"
                className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-[#122A5C] hover:text-white transition-all border border-slate-200"
                data-testid="testimonial-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    aria-label={`Kundenstimme ${index + 1} anzeigen`}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentTestimonial ? 'bg-[#E07000] w-8' : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                    data-testid={`testimonial-dot-${index}`}
                  />
                ))}
              </div>
              <button 
                onClick={nextTestimonial}
                aria-label="Naechste Kundenstimme anzeigen"
                className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-[#122A5C] hover:text-white transition-all border border-slate-200"
                data-testid="testimonial-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#122A5C] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[#E07000]/30 rounded-full blur-[150px] -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[150px] translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {/* Logo in CTA */}
            <motion.img
              src={LOGO_URL}
              alt="EN:POWER Logo ENPOWER"
              width={1120}
              height={160}
              loading="lazy"
              decoding="async"
              className="w-44 sm:w-56 md:w-64 h-auto mx-auto mb-8"
              style={{ 
                imageRendering: 'crisp-edges',
                filter: 'drop-shadow(0 4px 20px rgba(224, 112, 0, 0.25))'
              }}
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            />
            
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Lassen Sie uns über Ihr Projekt sprechen
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Ob Kleinspeicher, Batteriespeicher, Freifläche oder Dachfläche – wir finden die passende Lösung für Sie. Kostenlose Erstberatung, unverbindliches Angebot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/angebot-anfordern">
                <Button 
                  data-testid="final-cta-button"
                  className="bg-[#E07000] hover:bg-[#C56200] text-white rounded-full px-10 py-6 text-lg font-semibold btn-animate shadow-xl shadow-orange-600/30"
                >
                  Kostenlos Angebot anfordern
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/pachtrechner">
                <Button 
                  data-testid="final-calculator-button"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white hover:text-[#122A5C] rounded-full px-10 py-6 text-lg font-semibold transition-all"
                >
                  Pachtrechner starten
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
